import {
  createContext, useCallback, useContext, useLayoutEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Button, Flex, IconButton, Text,
} from '@chakra-ui/react';
import { LuArrowRight, LuChevronLeft } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import PhoneSheet, { usePhoneSheet } from '../../phone/PhoneSheet';
import { statementFlags } from '../repairsUI';

// Past this many statements the queue shows as one bar, not a segment each.
const MAX_SEGMENTS = 12;

const PILL_TONES = {
  good: { bg: 'rgba(21, 128, 61, 0.10)', color: 'phone.income', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  alert: { bg: 'phone.alertBg', color: 'phone.alertText' },
};

/** A short label at the phone's 13px: "Se queda", "Se quita", "No cuadra". */
export function PhonePill({ tone = 'alert', children }) {
  const style = PILL_TONES[tone] || PILL_TONES.alert;
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      h="24px"
      px="8px"
      borderRadius="full"
      textStyle="phoneMeta"
      fontWeight="600"
      whiteSpace="nowrap"
      {...style}
    >
      {children}
    </Box>
  );
}

const FooterContext = createContext(null);

/**
 * Puts its children in the footer of the phone checkup's sheet, which never
 * scrolls away: the statement on screen owns it (its review's button, its
 * "up next"). Outside that sheet, on the desktop, it renders nothing.
 */
export function CheckupSheetFooter({ children }) {
  const slot = useContext(FooterContext);
  const claim = slot?.claim;
  const release = slot?.release;
  useLayoutEffect(() => {
    if (!claim) return undefined;
    claim();
    return release;
  }, [claim, release]);
  return slot?.host ? createPortal(children, slot.host) : null;
}

// One line under the main button: "You can undo it afterwards."
export function FooterNote({ children }) {
  return <Text mt="8px" textStyle="phoneMeta" color="phone.muted" textAlign="center">{children}</Text>;
}

// Why the next statement needs a look, in one line.
function whyText(key, flags, t) {
  if (key === 'doesntAddUp') return t('checkup.card.doesntAddUp');
  if (key === 'reprocess') return t('checkup.card.reprocess');
  if (key === 'leftovers') {
    const count = flags?.leftover_rows;
    return t(count === 1 ? 'checkup.card.leftoversOne' : 'checkup.card.leftoversOther', { count });
  }
  return null;
}

/**
 * The footer of a statement once it is done: the next statement of the
 * checkup and why it needs a look, with Continue; or, after the last one, a
 * way out.
 */
export function PhoneNextFooter({ next, alone, onContinue, onFinish }) {
  const { t } = useTranslation('repairs');
  if (!next) {
    return (
      <>
        {!alone && <FooterNote>{t('checkup.done.allDone')}</FooterNote>}
        <Button variant="phonePrimary" w="100%" mt={alone ? 0 : '10px'} onClick={onFinish}>
          {t('checkup.phone.finish')}
        </Button>
      </>
    );
  }
  const attention = statementFlags(next.flags).find((flag) => flag.attention);
  const why = attention && whyText(attention.key, next.flags, t);
  return (
    <>
      <Text textStyle="phoneCaption" color="phone.muted">
        {t(next.left === 1 ? 'checkup.done.upNextOne' : 'checkup.done.upNextOther', { count: next.left })}
      </Text>
      <Flex align="center" gap="8px" mt="4px" minW={0}>
        <Text textStyle="phoneRowName" fontWeight="600" color="redesign.heading" noOfLines={1} minW={0}>{next.name}</Text>
        {attention && <PhonePill>{t(`flags.${attention.key}`)}</PhonePill>}
      </Flex>
      {why && <Text mt="2px" textStyle="phoneMeta" color="phone.muted">{why}</Text>}
      <Button variant="phonePrimary" w="100%" mt="10px" rightIcon={<LuArrowRight />} onClick={onContinue}>
        {t('checkup.done.continue')}
      </Button>
      <FooterNote>{t('checkup.phone.stop')}</FooterNote>
    </>
  );
}

// How far the checkup is: one segment per statement, the finished ones in
// green and the one on screen (or up next, once it is done) in blue.
function Progress({ segments }) {
  if (segments.length > MAX_SEGMENTS) {
    const done = segments.filter((segment) => segment === 'done').length;
    return (
      <Box mx="16px" mt="4px" h="4px" borderRadius="2px" bg="redesign.border" aria-hidden="true">
        <Box h="4px" borderRadius="2px" bg="phone.income" w={`${(done / segments.length) * 100}%`} transition="width 0.25s ease-out" />
      </Box>
    );
  }
  const colors = { done: 'phone.income', active: 'phone.link', todo: 'redesign.border' };
  return (
    <Flex gap="4px" px="16px" pt="4px" aria-hidden="true">
      {segments.map((segment, index) => (
        <Box key={index} flex="1" h="4px" borderRadius="2px" bg={colors[segment]} />
      ))}
    </Flex>
  );
}

// The back chevron: closing the sheet leaves the checkup.
function BackButton() {
  const { t } = useTranslation('repairs');
  const { close } = usePhoneSheet();
  return (
    <IconButton
      icon={<LuChevronLeft size={26} />}
      variant="ghost"
      borderRadius="full"
      color="redesign.text"
      minW="44px"
      minH="44px"
      aria-label={t('checkup.phone.back')}
      onClick={() => close()}
    />
  );
}

/**
 * The statement checkup on the phone: one full-height sheet over the app, as
 * the phone's review flow is, so neither the tab bar nor "+" sits beside the
 * decision. The header names the statement and where the checkup stands
 * ("Revisión 1 de 2"), with Skip on the right; the footer belongs to the
 * statement on screen (`CheckupSheetFooter`). Closing the sheet, from the
 * back chevron, a swipe down or a back swipe, calls `onClose`.
 */
export default function PhoneCheckupSheet({
  isOpen, onClose, title, subtitle, onSkip, segments, children,
}) {
  const { t } = useTranslation('repairs');
  const [claims, setClaims] = useState(0);
  const [host, setHost] = useState(null);
  const claim = useCallback(() => setClaims((count) => count + 1), []);
  const release = useCallback(() => setClaims((count) => count - 1), []);
  const slot = useMemo(() => ({ host, claim, release }), [host, claim, release]);

  return (
    <PhoneSheet
      id="statement-checkup"
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={title}
      subtitle={subtitle}
      headerLeft={<BackButton />}
      headerRight={onSkip ? (
        <Button variant="ghost" color="phone.link" fontSize="16px" fontWeight="600" minH="44px" px="12px" onClick={onSkip}>
          {t('checkup.phone.skip')}
        </Button>
      ) : null}
      footer={claims > 0 ? <Box ref={setHost} /> : undefined}
    >
      <FooterContext.Provider value={slot}>
        {segments?.length > 0 && <Progress segments={segments} />}
        <Box px="16px" pt="12px">{children}</Box>
      </FooterContext.Provider>
    </PhoneSheet>
  );
}
